import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SpaIcon from '@mui/icons-material/Spa';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import { formatDateDisplay } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function formatNumber(value) {
  return numberFormatter.format(Number(value || 0));
}

function formatDate(value) {
  return formatDateDisplay(value);
}

function getAmount(record) {
  return Number(record.total_amount || record.amount || 0);
}

function getWorkPlace(record) {
  return String(record.work_place || record.place || '').toLowerCase();
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 46,
            height: 46,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: soft,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function FarrierList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [page, setPage] = useState(0);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState(null);
  const [confirmationText, setConfirmationText] = useState('');

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/farrier-records`);
      const rows = (Array.isArray(response.data) ? response.data : []).filter(
        (record) => getWorkPlace(record) !== 'outside'
      );
      setRecords(rows.sort((a, b) => new Date(b.record_date || 0) - new Date(a.record_date || 0)));
    } catch (err) {
      setError(t('Failed_to_fetch_farrier_records') || 'Failed to fetch farrier records');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const summary = useMemo(() => {
    const amount = records.reduce((sum, record) => sum + getAmount(record), 0);
    const lastDate = records[0]?.record_date || null;
    const horses = new Set(records.map((record) => record.inside_horse_id || record.horse_id || record.horse_name).filter(Boolean)).size;
    return { total: records.length, horses, amount, lastDate };
  }, [records]);

  const filteredRecords = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return records.filter((record) => {
      const searchOk =
        !needle ||
        [
          record.record_id,
          record.horse_name,
          record.procedure_name,
          record.medication_name,
          record.notes,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      return searchOk;
    });
  }, [records, search]);

  const pagedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleDeleteClick = (recordId) => {
    setDeleteRecordId(recordId);
    setDeleteConfirmationOpen(true);
    setConfirmationText('');
  };

  const confirmDelete = async () => {
    if (confirmationText.toLowerCase() !== 'delete') {
      setError(t('Please_type_DELETE_to_confirm') || 'Please type delete to confirm');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await api.delete(`${apiUrl}/farrier-records/${deleteRecordId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setRecords((prev) => prev.filter((record) => record.record_id !== deleteRecordId));
      setDeleteConfirmationOpen(false);
      setConfirmationText('');
    } catch (err) {
      setError(t('Failed_to_delete_record') || 'Failed to delete record');
    }
  };

  return (
    <Box sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1540, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('Farrier_List') || 'Farrier Records'}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {t('farrier_records_hint') ||
                  'Track hoof-care work, service history, and charge readiness by horse.'}
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchRecords}
                sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 800 }}
              >
                {t('refresh') || 'Refresh'}
              </Button>
              <Button
                variant="contained"
                startIcon={<HomeOutlinedIcon />}
                onClick={() => navigate('/AddFarrierRecord')}
                sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
              >
                {t('add_record') || 'Add Record'}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <MetricCard icon={<SpaIcon />} title={t('all_records') || 'All records'} value={formatNumber(summary.total)} helper={t('currentData') || 'Current data'} />
          <MetricCard icon={<HomeOutlinedIcon />} title={t('registered_horses') || 'Registered horses'} value={formatNumber(summary.horses)} helper={t('stable_cases') || 'Stable cases'} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<SpaIcon />} title={t('latest_record') || 'Latest record'} value={<span className="date-ltr">{formatDate(summary.lastDate)}</span>} helper={t('date') || 'Date'} color={COLORS.rose} soft={COLORS.roseSoft} />
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={1.25}
            sx={{ p: 2 }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
              <TextField
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder={t('search') || 'Search'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: { xs: '100%', sm: 320 } }}
              />
            </Stack>
            <Chip
              label={`${formatNumber(filteredRecords.length)} ${t('records') || 'records'}`}
              sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 900, alignSelf: { xs: 'flex-start', md: 'center' } }}
            />
          </Stack>

          <TableContainer sx={smartTableContainerSx}>
            <Table
              size="small"
              sx={{
                ...smartTableSx,
                tableLayout: 'fixed',
                minWidth: 900,
                '& .MuiTableCell-root': {
                  verticalAlign: 'middle',
                },
              }}
            >
              <TableHead sx={smartTableHeadSx}>
                <TableRow>
                  <TableCell align="center" width={56}>#</TableCell>
                  <TableCell align="center" width={76}>ID</TableCell>
                  <TableCell align="center" width={132}>{t('Date') || 'Date'}</TableCell>
                  <TableCell align="center" width={240}>{t('Horse_Name') || 'Horse'}</TableCell>
                  <TableCell align="center" width={205}>{t('Actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : pagedRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: COLORS.muted }}>
                      {t('no_data') || 'No data'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRecords.map((record, index) => (
                    <TableRow key={record.record_id} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell align="center">{record.record_id}</TableCell>
                      <TableCell align="center"><span className="date-ltr">{formatDate(record.record_date)}</span></TableCell>
                      <TableCell align="center" sx={{ maxWidth: 240 }}>
                        <Typography
                          title={record.horse_name || '-'}
                          sx={{
                            color: COLORS.text,
                            fontWeight: 900,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {record.horse_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={t('view') || 'View'}>
                          <IconButton size="small" onClick={() => navigate(`/FarrierDetails/${record.record_id}`)} sx={{ color: COLORS.blue, mx: 0.25 }}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('edit') || 'Edit'}>
                          <IconButton size="small" onClick={() => navigate(`/EditFarrierRecord/${record.record_id}`)} sx={{ color: COLORS.gold, mx: 0.25 }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('delete') || 'Delete'}>
                          <IconButton size="small" onClick={() => handleDeleteClick(record.record_id)} sx={{ color: COLORS.rose, mx: 0.25 }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredRecords.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 15, 25, 50]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
          />
        </Paper>
      </Box>

      <Dialog open={deleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)}>
        <DialogTitle>{t('Confirm_Deletion') || 'Confirm deletion'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: COLORS.text, mb: 1 }}>
            {t('Please_type_DELETE_to_confirm') || 'Please type delete to confirm.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={confirmationText}
            onChange={(event) => setConfirmationText(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>{t('Cancel') || 'Cancel'}</Button>
          <Button onClick={confirmDelete} sx={{ color: COLORS.rose, fontWeight: 900 }}>
            {t('Delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
